import { useEffect, useState, useContext } from 'react';
import { Variant } from '@dayetopia/types';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import { Context } from '@components/Context';
import {
  CBD_SUPER_TAMPON_KEY,
  CBD_SUPER_TAMPON_NAME,
  CBD_REGULAR_TAMPON_KEY,
  CBD_REGULAR_TAMPON_NAME,
  NAKED_SUPER_TAMPON_KEY,
  NAKED_SUPER_TAMPON_NAME,
  NAKED_REGULAR_TAMPON_KEY,
  NAKED_REGULAR_TAMPON_NAME
} from '@constants';
import { CartItem, CartItemType } from '@contracts';
import { ButtonTypesCatalogue, ProductData } from '@data/storefront';
import { getBoxPrice } from '@utils/boxPrices';
import { getPriceText } from './price';
import { handlePriceSetting, getIsPresetTamponBox, getTamponConfigAndCounts } from './product';

interface UseTrackedCartItemProps {
  data: ProductData;
  handleTracking?: (cartItem: CartItem) => void;
}

const useTrackedCartItem = ({ data, handleTracking }: UseTrackedCartItemProps) => {
  const [price, setPrice] = useState(-1);
  const [priceText, setPriceText] = useState('');
  const [isTracked, setIsTracked] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [boxConfig, setBoxConfig] = useState<Variant | undefined>(undefined);
  const [cbdTamponPrice, setCbdTamponPrice] = useState(0);
  const [nakedTamponPrice, setNakedTamponPrice] = useState(0);
  const [cartItem, setCartItem] = useState<CartItem | null>();
  const fromProductPriceCode = data?.from_price_product_code?.text;
  const isPresetTamponBox = getIsPresetTamponBox(data.product_code_override?.text);
  const { t } = useTranslation();
  const { state } = useContext(Context);
  const { currency, country } = state;

  useEffect(() => {
    if (isPresetTamponBox) {
      getTamponPrices();
      updateBoxConfig();
    } else {
      setLoaded(true);
    }

    handlePriceSetting(data, country, currency, setPrice, setPriceText, t, !!isPresetTamponBox, fromProductPriceCode);
    generateCartItem();
  }, [currency]);

  useEffect(() => {
    if (isTracked) return;
    const currentCartItem = {
      id: data.product_code ?? data.product_code_override,
      type: data.button_type === ButtonTypesCatalogue.ChooseABox ? CartItemType.Subscription : CartItemType.OneOffPurchase,
      price,
      productCode: data.product_code ?? data.product_code_override,
      quantity: 1
    } as CartItem;

    if (currentCartItem && price !== 0) {
      handleTracking?.(currentCartItem);
      setIsTracked(true);
    }
  }, [price]);

  useEffect(() => {
    generateCartItem();
  }, [boxConfig, price]);

  const getTamponPrices = async () => {
    try {
      const cbdPrice = await getBoxPrice({ cbdregular: 1 } as Variant, currency, country);
      setCbdTamponPrice(cbdPrice);
      const nakedPrice = await getBoxPrice({ nakedregular: 1 } as Variant, currency, country);
      setNakedTamponPrice(nakedPrice);
      setLoaded(true);
    } catch (error) {
      console.error(error);
      setLoaded(false);
    }
  };

  const updateBoxConfig = async () => {
    const tamponBoxConfigString = data.product_code_override?.text?.split('_').pop();
    if (!tamponBoxConfigString) return;
    const { config } = getTamponConfigAndCounts(tamponBoxConfigString);
    setBoxConfig(config);

    const boxPrice = await getBoxPrice(config, currency, country);
    if (boxPrice) {
      setPrice(boxPrice);
      setPriceText(getPriceText(boxPrice, country, currency, t, false, false).fullString);
    }
  };

  const getBoxItems = () => {
    const { cbdsuper, cbdregular, nakedsuper, nakedregular } = boxConfig;
    return [
      { count: cbdsuper, key: CBD_SUPER_TAMPON_KEY, name: CBD_SUPER_TAMPON_NAME, price: cbdTamponPrice },
      { count: cbdregular, key: CBD_REGULAR_TAMPON_KEY, name: CBD_REGULAR_TAMPON_NAME, price: cbdTamponPrice },
      { count: nakedsuper, key: NAKED_SUPER_TAMPON_KEY, name: NAKED_SUPER_TAMPON_NAME, price: nakedTamponPrice },
      { count: nakedregular, key: NAKED_REGULAR_TAMPON_KEY, name: NAKED_REGULAR_TAMPON_NAME, price: nakedTamponPrice }
    ];
  };

  const generateBoxSKU = () => {
    const { cbdsuper, cbdregular, nakedsuper, nakedregular } = boxConfig;
    return `${cbdregular}${cbdsuper}${nakedregular}${nakedsuper}`;
  };

  const generateCartItem = () => {
    if (isPresetTamponBox && boxConfig) {
      const boxItems = getBoxItems();
      const boxSKU = generateBoxSKU();
      setCartItem({
        boxItems,
        id: `tampon_box_${boxSKU}`,
        type: CartItemType.OneOffPurchase,
        price,
        productCode: 'tampon_box',
        quantity: 1,
        editable: true,
        unique: false
      } as CartItem);
    } else {
      const productCode =
        data.product_code_override?.text && data.product_code_override?.text !== '' ? data.product_code_override?.text : data.product_code;
      setCartItem(new CartItem(productCode, productCode, price));
    }
  };

  return { price, priceText, loaded, boxConfig, cartItem };
};

export default useTrackedCartItem;
